// ─── Reveal Page ─────────────────────────────────────────────
// /reveal/:token — The cinematic "Morning After" experience
// No login. Token-based. Emotional, not functional.
//
// States: loading → opening → love → transition → photos → ending → grid

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function resolveUrl(url) {
  if (!url) return url;
  if (url.startsWith('/')) return `${API}${url}`;
  return url;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return dateStr; }
}

// ─── Photo sampling: first 3 special + timeline buckets + variety guards ──
function samplePhotos(photos, max = 18) {
  if (photos.length === 0) return [];
  if (photos.length <= max) return photos;

  const sorted = [...photos].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  // Skip first 2 noisy uploads if enough photos
  const skip = sorted.length > 10 ? Math.min(2, sorted.length - 1) : 0;
  const usable = sorted.slice(skip);
  if (usable.length <= max) return usable;

  const selectedIds = new Set();
  const selected = [];
  let prevGuest = null;
  let prevTable = null;

  // Helper: pick best from a pool
  const pickBest = (pool) => {
    let best = null;
    let bestScore = -1;
    for (const p of pool) {
      if (selectedIds.has(p.id)) continue;
      let score = 0;
      if (p.favorited) score += 10;
      if (p.guest_name && p.guest_name !== prevGuest) score += 5;
      if (p.table_name && p.table_name !== prevTable) score += 2;
      score += 0.1;
      if (score > bestScore) { bestScore = score; best = p; }
    }
    return best;
  };

  const addPick = (photo) => {
    if (!photo) return;
    selected.push(photo);
    selectedIds.add(photo.id);
    prevGuest = photo.guest_name;
    prevTable = photo.table_name;
  };

  // ─── Part 1: First 3 photos (special windows) ──────────
  const earlyEnd = Math.ceil(usable.length * 0.3);
  const midEnd = Math.ceil(usable.length * 0.5);
  const earlyWindow = usable.slice(0, earlyEnd);
  const midWindow = usable.slice(earlyEnd, midEnd);

  // Photo 1: best from early window (arrival)
  addPick(pickBest(earlyWindow));
  // Photo 2: best from early window (different guest/table)
  addPick(pickBest(earlyWindow));
  // Photo 3: best from mid window (event opening up)
  addPick(pickBest(midWindow.length > 0 ? midWindow : earlyWindow));

  // ─── Part 2: Remaining slots via time buckets ──────────
  const remaining = usable.filter(p => !selectedIds.has(p.id));
  const slotsLeft = max - selected.length;

  if (slotsLeft > 0 && remaining.length > 0) {
    const bucketSize = Math.ceil(remaining.length / slotsLeft);
    for (let i = 0; i < slotsLeft; i++) {
      const start = i * bucketSize;
      const end = Math.min(start + bucketSize, remaining.length);
      if (start >= remaining.length) break;
      const bucket = remaining.slice(start, end);
      addPick(pickBest(bucket));
    }
  }

  // ─── Final sort chronological ──────────────────────────
  selected.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  return selected;
}

// ─── Note sampling: smart quality scoring (for auto-fallback only) ──
// Used only when organizer hasn't manually curated (data.curated_notes=false).
// Goal: 5–8 notes with guest diversity, specific language, moderate length.
const GENERIC_TOKENS = [
  'congrats','congratulations','best wishes','all the best','best of luck',
  'wishing you','happy','love','cheers','enjoy','lovely','amazing','beautiful day',
];
function sampleNotes(notes, max = 8, min = 5) {
  const messages = notes.filter(n => n.wish_type === 'message' && n.message && n.message.trim().length > 0);
  if (messages.length <= min) return messages;

  // Score each message:
  //  + specificity (presence of names, places, memories — proxied by presence of longer non-generic words)
  //  + guest diversity (prefer unseen guests)
  //  − genericness (if message maps mostly to GENERIC_TOKENS)
  //  moderate length (20–220 chars sweet-spot)
  const scored = messages.map(m => {
    const text = (m.message || '').trim().toLowerCase();
    const len = text.length;
    let score = 0;

    // Length scoring: sweet spot around 60–180
    if (len >= 20 && len <= 260) score += 3;
    if (len >= 60 && len <= 180) score += 2;
    if (len < 20) score -= 3;
    if (len > 320) score -= 1;

    // Generic penalty
    const genericHits = GENERIC_TOKENS.filter(tok => text.includes(tok)).length;
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length > 0 && genericHits / words.length > 0.3) score -= 2;
    if (genericHits === 0 && words.length >= 6) score += 1.5;

    // Specificity: contains quoted name/place proxies (capitalised words ≥2 chars)
    const capitalWords = (m.message.match(/\b[A-Z][a-zA-Z]{2,}\b/g) || []).length;
    if (capitalWords >= 1) score += 1;

    // Small noise to break ties
    score += Math.random() * 0.1;
    return { item: m, score };
  });

  // Sort by score desc, then enforce guest diversity greedily
  scored.sort((a, b) => b.score - a.score);
  const seenGuests = new Set();
  const picked = [];
  for (const s of scored) {
    if (picked.length >= max) break;
    const g = s.item.guest_name || '__anon__';
    if (seenGuests.has(g)) continue;
    seenGuests.add(g);
    picked.push(s.item);
  }
  // If still under min, allow repeated guests (fill by score order)
  if (picked.length < min) {
    for (const s of scored) {
      if (picked.length >= min) break;
      if (!picked.includes(s.item)) picked.push(s.item);
    }
  }
  // Keep chronological for a natural arc
  picked.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  return picked;
}

// ─── Keep feedback whisper — portaled, shared across all phases ──
// Modes: 'first' | 'second' | 'third' | 'removed'
function KeepFeedbackOverlay({ mode, onViewKeeps }) {
  if (!mode) return null;
  const isThird = mode === 'third';
  const isRemoved = mode === 'removed';
  return createPortal(
    <div
      className="fixed top-8 left-0 right-0 text-center z-[500] pointer-events-none"
      style={{ animation: isRemoved ? 'keepWhisper 1400ms ease forwards' : 'keepWhisper 3200ms ease forwards' }}
      data-testid={`keep-feedback-${mode}`}
    >
      <p className="text-white/80 text-sm font-light italic tracking-wide">
        {isRemoved ? 'Removed' : 'Saved'}
      </p>
      {mode === 'first' && (
        <p className="text-white/45 text-[12px] tracking-wide mt-1.5">You&rsquo;ll find this in saved moments</p>
      )}
      {isThird && (
        <button
          onClick={(e) => { e.stopPropagation(); onViewKeeps?.(); }}
          className="text-white/55 text-[12px] tracking-wide mt-1.5 underline-offset-4 hover:underline pointer-events-auto"
          data-testid="keep-feedback-view-link"
        >
          View saved moments &rarr;
        </button>
      )}
      <style>{`
        @keyframes keepWhisper {
          0% { opacity: 0; transform: translateY(-4px); }
          12% { opacity: 1; transform: translateY(0); }
          82% { opacity: 1; }
          100% { opacity: 0; transform: translateY(-2px); }
        }
      `}</style>
    </div>,
    document.body
  );
}

// ─── Kept voice item — small inline player for the kept-seq screen ──
function KeptVoiceItem({ item, audioUrl }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const fmt = (s) => {
    const m = Math.floor((s || 0) / 60);
    const r = Math.max(0, Math.floor((s || 0) % 60));
    return `${m}:${r.toString().padStart(2, '0')}`;
  };
  const toggle = (e) => {
    e.stopPropagation();
    const a = ref.current; if (!a) return;
    if (playing) { a.pause(); setPlaying(false); return; }
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };
  return (
    <div className="flex flex-col items-center text-center max-w-sm" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={toggle}
        className="w-20 h-20 rounded-full flex items-center justify-center border border-white/20 bg-white/5 hover:bg-white/10 transition-colors mb-6"
        data-testid="kept-voice-play"
        aria-label={playing ? 'Pause' : 'Play'}
      >
        <span className="text-white/85 text-xl">{playing ? '❘❘' : '▶'}</span>
      </button>
      <p className="text-white/85 text-lg font-light italic">In {item.guest_name}&rsquo;s voice</p>
      <p className="text-white/30 text-xs mt-2 tracking-wide" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(item.duration_sec)}</p>
      <audio ref={ref} src={audioUrl} preload="metadata" onEnded={() => setPlaying(false)} />
    </div>
  );
}

// ─── In their voices — browse surface (tap to play, no auto-play, no chaining) ──
function VoicesFullSurface({ voices, onBack }) {
  const [activeId, setActiveId] = useState(null);
  const audioRefs = useRef({});

  const fmtDuration = (s) => {
    const m = Math.floor((s || 0) / 60);
    const r = Math.max(0, Math.floor((s || 0) % 60));
    return `${m}:${r.toString().padStart(2, '0')}`;
  };

  const playPause = (id) => {
    const a = audioRefs.current[id];
    if (!a) return;
    if (activeId === id) {
      // Tap active — pause
      a.pause();
      setActiveId(null);
      return;
    }
    // Pause any other
    Object.entries(audioRefs.current).forEach(([k, el]) => {
      if (k !== id && el) { try { el.pause(); el.currentTime = 0; } catch { /* */ } }
    });
    a.play().then(() => setActiveId(id)).catch(() => setActiveId(null));
  };

  const sorted = [...voices].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  return (
    <div className="min-h-screen bg-black px-6 pt-12 pb-24" data-testid="reveal-voices-full">
      <button
        onClick={onBack}
        className="text-white/40 text-xs mb-10 block"
      >
        &larr; Back
      </button>
      <p className="text-white/40 text-xs tracking-[0.2em] uppercase text-center mb-10">What they said</p>
      <div className="max-w-md mx-auto space-y-3">
        {sorted.map((v, i) => {
          const isPlaying = activeId === v.id;
          const audioFullUrl = resolveUrl(v.audio_url);
          return (
            <button
              key={v.id}
              onClick={() => playPause(v.id)}
              className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl border transition-all ${
                isPlaying ? 'bg-white/8 border-white/20' : 'bg-transparent border-white/10 hover:border-white/20'
              }`}
              style={{
                opacity: 0,
                animation: `voiceListFade 0.5s ease ${Math.min(i, 8) * 0.04}s forwards`,
              }}
              data-testid={`reveal-voice-item-${v.id}`}
            >
              <span className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center border border-white/20 bg-white/5">
                {isPlaying ? (
                  <span className="text-white/90 text-sm">❘❘</span>
                ) : (
                  <span className="text-white/85 text-base ml-0.5">▶</span>
                )}
              </span>
              <span className="flex-1 text-left">
                <span className="block text-white/85 text-base font-light italic">In {v.guest_name}&rsquo;s voice</span>
                <span className="block text-white/40 text-xs mt-0.5 tracking-wide" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmtDuration(v.duration_sec)}
                </span>
              </span>
              <audio
                ref={(el) => { audioRefs.current[v.id] = el; }}
                src={audioFullUrl}
                preload="metadata"
                onEnded={() => setActiveId(null)}
              />
            </button>
          );
        })}
      </div>
      <style>{`@keyframes voiceListFade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}

export default function RevealPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === '1';
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState('loading'); // loading|opening|love|transition|photos|ending|grid
  const [loveIndex, setLoveIndex] = useState(0);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [photoVisible, setPhotoVisible] = useState(true);
  const [textVisible, setTextVisible] = useState(false);
  const [tapFeedback, setTapFeedback] = useState(false);
  const [shareState, setShareState] = useState('idle'); // idle | copied
  // Kept items (couple's quiet "hold onto this" set)
  const [kept, setKept] = useState([]);
  const [lightboxIdx, setLightboxIdx] = useState(null); // index into allMoments when in grid phase
  const [limitHint, setLimitHint] = useState(false);
  const [keptIdx, setKeptIdx] = useState(0);
  const [keptFade, setKeptFade] = useState(true);
  // Send-it-to-me state
  const [sendState, setSendState] = useState('idle'); // idle | input | sending | sent | error
  const [sendEmail, setSendEmail] = useState('');
  const [sentTo, setSentTo] = useState('');
  // Voice playback state in love phase
  const [voicePlayState, setVoicePlayState] = useState('idle'); // 'idle' | 'playing' | 'ended'
  const loveAudioRef = useRef(null);
  // Heart feedback ("Your keeps" ambient teach) + remove feedback
  // modes: 'first' | 'second' | 'third' | 'removed' | null
  const [keepFeedback, setKeepFeedback] = useState(null);
  const keepFeedbackTimerRef = useRef(null);
  const loveTimerRef = useRef(null);
  const preloadRef = useRef(null);
  const limitTimerRef = useRef(null);

  // Fetch data
  useEffect(() => {
    const fetch = async () => {
      try {
        const url = `${API}/api/reveal/${token}${isPreview ? '?preview=1' : ''}`;
        const res = await axios.get(url);
        setData(res.data);
        setKept(res.data?.kept || []);
        setPhase('opening');
      } catch {
        setPhase('error');
      }
    };
    fetch();
  }, [token]);

  const isKept = useCallback((type, id) => kept.some(k => k.type === type && k.id === id), [kept]);

  const showLimitHint = () => {
    setLimitHint(true);
    clearTimeout(limitTimerRef.current);
    limitTimerRef.current = setTimeout(() => setLimitHint(false), 2200);
  };

  const toggleKeep = async (type, id) => {
    const already = isKept(type, id);
    if (!already && kept.length >= 10) {
      showLimitHint();
      return;
    }
    // Optimistic
    const next = already
      ? kept.filter(k => !(k.type === type && k.id === id))
      : [...kept, { type, id, ts: new Date().toISOString() }];
    setKept(next);

    // Ambient feedback — progressive teach tracks MEANINGFUL keeps, not tap events.
    // If they keep then un-keep, the counter rolls back so the next keep is treated
    // as their true first. Counter persists per reveal token for the session:
    //   1st meaningful keep → "Kept / You'll find this in your keeps"
    //   2nd                  → "Kept"
    //   3rd+                 → "Kept / View your keeps →"
    try {
      const key = `sitoa_keep_teach_${token}`;
      const prev = Number(sessionStorage.getItem(key) || '0');
      if (!already) {
        const nextCount = prev + 1;
        sessionStorage.setItem(key, String(nextCount));
        if (keepFeedbackTimerRef.current) clearTimeout(keepFeedbackTimerRef.current);
        const mode = nextCount === 1 ? 'first' : nextCount === 2 ? 'second' : 'third';
        setKeepFeedback(mode);
        const hold = mode === 'third' ? 3600 : 2500;
        keepFeedbackTimerRef.current = setTimeout(() => setKeepFeedback(null), hold);
      } else {
        // Un-keep — roll the counter back, floor at 0. Silent (no teach, no "Removed"
        // outside of kept-seq — would feel scolding on the grid/lightbox).
        const nextCount = Math.max(0, prev - 1);
        sessionStorage.setItem(key, String(nextCount));
      }
    } catch { /* private mode / storage blocked */ }

    try {
      const res = await axios.post(`${API}/api/reveal/${token}/keep`, { type, id, keep: !already });
      setKept(res.data?.kept || next);
      if (res.data?.at_limit) showLimitHint();
    } catch {
      // rollback
      setKept(kept);
    }
  };

  // Un-keep from inside kept-seq — emits a "Removed" whisper instead of the teach overlay
  const unkeepInSequence = async (type, id) => {
    if (keepFeedbackTimerRef.current) clearTimeout(keepFeedbackTimerRef.current);
    setKeepFeedback('removed');
    keepFeedbackTimerRef.current = setTimeout(() => setKeepFeedback(null), 1400);
    await toggleKeepRaw(type, id);
  };

  // Raw toggle without teach overlay (used by un-keep in kept-seq)
  const toggleKeepRaw = async (type, id) => {
    const already = isKept(type, id);
    if (!already && kept.length >= 10) { showLimitHint(); return; }
    const next = already
      ? kept.filter(k => !(k.type === type && k.id === id))
      : [...kept, { type, id, ts: new Date().toISOString() }];
    setKept(next);
    // Roll teach counter in sync — un-keep decrements, keep increments (silent here).
    try {
      const key = `sitoa_keep_teach_${token}`;
      const prev = Number(sessionStorage.getItem(key) || '0');
      sessionStorage.setItem(key, String(already ? Math.max(0, prev - 1) : prev + 1));
    } catch { /* */ }
    try {
      const res = await axios.post(`${API}/api/reveal/${token}/keep`, { type, id, keep: !already });
      setKept(res.data?.kept || next);
    } catch { setKept(kept); }
  };

  const allLove = data?.love || [];
  const curatedNotes = !!data?.curated_notes;
  // Mixed Love phase (text + voice intermixed) — organizer-defined.
  // Server returns `love_mixed: [{type, data}]`. Falls back to text-only when not curated.
  const loveMixedFromServer = data?.love_mixed || null;
  const loveMixedCurated = !!data?.love_mixed_curated;
  // If organizer curated, respect exact order (server already ordered).
  // Else split: keep hearts as-is (simple chronological) and smart-sample messages.
  const hearts = allLove.filter(n => n.wish_type === 'heart');
  const messages = allLove.filter(n => n.wish_type === 'message');
  const textOnlyLove = curatedNotes
    ? allLove
    : [...hearts, ...sampleNotes(messages)];
  // Build the mixed sequence used by the love phase. Voice items are rendered
  // differently (tap-to-play card with breathing ring; advance after playback ends).
  const loveMixed = (loveMixedCurated && Array.isArray(loveMixedFromServer) && loveMixedFromServer.length > 0)
    ? loveMixedFromServer
    : textOnlyLove.map(n => ({ type: 'note', data: n }));
  // Backwards-compat: legacy `love` reference for old code paths
  const love = textOnlyLove;
  const allVoices = data?.voices || [];
  const allMoments = data?.moments || [];
  const selectedPhotos = samplePhotos(allMoments);
  const hasLove = loveMixed.length > 0;
  const hasPhotos = selectedPhotos.length > 0;
  const hasAnyNotes = messages.length > 0;
  const hasVoices = allVoices.length > 0;

  // Resolve kept items → actual content, preserving order
  const allLoveRaw = data?.love || [];
  // Use full unfiltered lists (we also need to fall back to full collection for notes not in curated set)
  const notesById = {};
  allLoveRaw.forEach(l => { notesById[l.id] = l; });
  const photosById = {};
  allMoments.forEach(p => { photosById[p.id] = p; });
  const voicesById = {};
  allVoices.forEach(v => { voicesById[v.id] = v; });
  const resolvedKept = kept
    .map(k => {
      if (k.type === 'photo') {
        const p = photosById[k.id];
        return p ? { kind: 'photo', item: p, ref: k } : null;
      }
      if (k.type === 'note') {
        const n = notesById[k.id];
        return n && n.wish_type === 'message' ? { kind: 'note', item: n, ref: k } : null;
      }
      if (k.type === 'voice') {
        const v = voicesById[k.id];
        return v ? { kind: 'voice', item: v, ref: k } : null;
      }
      return null;
    })
    .filter(Boolean);
  const keptCount = resolvedKept.length;
  const hasAnyKept = keptCount >= 1;
  const hasEnoughKept = keptCount >= 3;

  // Persist a reordered / pruned kept list after manual edit
  const saveKeptOrder = async (nextItems) => {
    const payload = nextItems.map(k => ({ type: k.type, id: k.id }));
    try {
      const res = await axios.put(`${API}/api/reveal/${token}/kept-order`, { items: payload });
      setKept(res.data?.kept || nextItems);
    } catch {
      // rollback by refetching would be ideal; just reapply local
      setKept(kept);
    }
  };

  // Determine flow
  const getNextPhase = useCallback((current) => {
    if (current === 'opening') return hasLove ? 'love' : hasPhotos ? 'transition' : 'ending';
    if (current === 'love') return hasPhotos ? 'transition' : 'ending';
    if (current === 'transition') return 'photos';
    if (current === 'photos') return 'ending';
    return null;
  }, [hasLove, hasPhotos]);

  // Love auto-advance (3s for text/heart, voice items wait for tap+playback to end)
  useEffect(() => {
    if (phase !== 'love') return;
    const item = loveMixed[loveIndex];
    // Voice items: do NOT auto-advance — guest must tap to play; advance hooks into onEnded.
    if (item && item.type === 'voice') return;
    loveTimerRef.current = setTimeout(() => {
      if (loveIndex < loveMixed.length - 1) {
        setLoveIndex(i => i + 1);
      } else {
        setPhase(getNextPhase('love'));
      }
    }, 3000);
    return () => clearTimeout(loveTimerRef.current);
  }, [phase, loveIndex, loveMixed, getNextPhase]);

  // Reset voice playback state when love item changes
  useEffect(() => {
    setVoicePlayState('idle');
    if (loveAudioRef.current) {
      try { loveAudioRef.current.pause(); loveAudioRef.current.currentTime = 0; } catch { /* */ }
    }
  }, [loveIndex]);

  // Transition auto-advance (2.5s)
  useEffect(() => {
    if (phase !== 'transition') return;
    const t = setTimeout(() => setPhase('photos'), 2500);
    return () => clearTimeout(t);
  }, [phase]);

  // Photo text delay (140ms after photo appears)
  useEffect(() => {
    if (phase !== 'photos') return;
    setTextVisible(false);
    const t = setTimeout(() => setTextVisible(true), 140);
    return () => clearTimeout(t);
  }, [phase, photoIndex]);

  // Preload next photo
  useEffect(() => {
    if (phase !== 'photos' || photoIndex >= selectedPhotos.length - 1) return;
    const next = selectedPhotos[photoIndex + 1];
    if (next) {
      const img = new Image();
      img.src = resolveUrl(next.image_url);
      preloadRef.current = img;
    }
  }, [phase, photoIndex, selectedPhotos]);

  // ─── Love tap handler ──
  const handleLoveTap = () => {
    clearTimeout(loveTimerRef.current);
    if (loveIndex < loveMixed.length - 1) {
      setLoveIndex(i => i + 1);
    } else {
      setPhase(getNextPhase('love'));
    }
  };

  // ─── Dynamic pacing from EXIF deltas ──
  // Larger gap between captured_at of prev vs current photo → longer cross-fade/hold.
  // This is atmospheric only — never below 180ms, never above 520ms.
  const paceFor = (idx) => {
    if (idx <= 0) return 250;
    const prev = selectedPhotos[idx - 1];
    const curr = selectedPhotos[idx];
    if (!prev || !curr) return 250;
    const pT = prev.effective_time || prev.captured_at || prev.timestamp;
    const cT = curr.effective_time || curr.captured_at || curr.timestamp;
    if (!pT || !cT) return 250;
    const pMs = Date.parse(pT);
    const cMs = Date.parse(cT);
    if (isNaN(pMs) || isNaN(cMs)) return 250;
    const deltaMin = Math.abs(cMs - pMs) / 60000;
    // Map delta: <1min=180ms, 15min=300ms, 60min=420ms, 180min+=520ms
    if (deltaMin < 1) return 180;
    if (deltaMin < 15) return 180 + (deltaMin / 15) * 120;        // 180→300
    if (deltaMin < 60) return 300 + ((deltaMin - 15) / 45) * 120;  // 300→420
    if (deltaMin < 180) return 420 + ((deltaMin - 60) / 120) * 100; // 420→520
    return 520;
  };

  // ─── Time-of-day ambience ──
  // Returns { amber, temp } — amber is a 0..1 strength for a warm vignette,
  // temp is a very small sepia filter nudge. Peaks near midnight, zero during daytime.
  const ambienceFor = (item) => {
    const t = item?.effective_time || item?.captured_at || item?.timestamp;
    if (!t) return { amber: 0, temp: 0 };
    const ms = Date.parse(t);
    if (isNaN(ms)) return { amber: 0, temp: 0 };
    const d = new Date(ms);
    const h = d.getHours() + d.getMinutes() / 60;
    // Circular hour distance from 0 (midnight). Night peak 22..02.
    const distFromMidnight = Math.min(Math.abs(h - 0), Math.abs(h - 24));
    // Evening ramp (19..22): 0→1. Midnight peak: 1. Dawn ramp (02..06): 1→0. Day: 0.
    let amber;
    if (h >= 19 && h < 22) amber = (h - 19) / 3;
    else if (h >= 22 || h < 2) amber = 1;
    else if (h >= 2 && h < 6) amber = 1 - (h - 2) / 4;
    else amber = 0;
    // Cap so it never becomes obvious
    amber = Math.max(0, Math.min(1, amber)) * 0.45;
    const temp = distFromMidnight < 3 ? 0.08 : 0;
    return { amber, temp };
  };

  // ─── Photo tap handler (left 30% = prev, right 70% = next) ──
  const handlePhotoTap = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;

    // Micro tap feedback
    setTapFeedback(true);
    setTimeout(() => setTapFeedback(false), 80);

    if (pct < 0.3 && photoIndex > 0) {
      // Previous
      const pace = paceFor(photoIndex);
      setPhotoVisible(false);
      setTimeout(() => {
        setPhotoIndex(i => i - 1);
        setPhotoVisible(true);
      }, pace);
    } else {
      // Next
      if (photoIndex < selectedPhotos.length - 1) {
        const pace = paceFor(photoIndex + 1);
        setPhotoVisible(false);
        setTimeout(() => {
          setPhotoIndex(i => i + 1);
          setPhotoVisible(true);
        }, pace);
      } else {
        setPhase('ending');
      }
    }
  };

  // ─── Share reveal ──
  const handleShareReveal = async () => {
    // Prefer friendly URL (sitoa.studio/sofia-miguel/morning-reveal/{token}) when slug is known
    const slug = data?.event_slug;
    const friendlyUrl = slug
      ? `${window.location.origin}/${slug}/morning-reveal/${token}`
      : `${API}/api/r/${token}`;
    const shareData = {
      title: `${data?.event_name || 'Our wedding'} — the morning after`,
      text: 'A small window into our day, through their eyes.',
      url: friendlyUrl,
    };
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share(shareData);
      } catch { /* user cancelled — stay quiet */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(friendlyUrl);
      setShareState('copied');
      setTimeout(() => setShareState('idle'), 1800);
    } catch { /* noop */ }
  };

  // ─── Send kept book via email ──
  const handleSendBook = async (explicitEmail = null) => {
    const emailToUse = explicitEmail || sendEmail || null;
    // If no known email and no explicit email, reveal input
    if (!data?.has_known_email && !emailToUse) {
      setSendState('input');
      return;
    }
    setSendState('sending');
    try {
      const body = emailToUse ? { email: emailToUse } : {};
      const res = await axios.post(`${API}/api/reveal/${token}/book/send`, body);
      setSentTo(res.data?.sent_to || data?.recipient_hint || emailToUse || '');
      setSendState('sent');
      // Fade confirmation after 4s
      setTimeout(() => setSendState('idle'), 4500);
    } catch {
      setSendState('error');
      setTimeout(() => setSendState(data?.has_known_email ? 'idle' : 'input'), 2500);
    }
  };

  // ─── RENDER ─────────────────────────────────────────────────

  if (phase === 'loading') {
    return <div className="fixed inset-0 bg-black" />;
  }

  if (phase === 'error') {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <p className="text-white/40 text-sm">This reveal is no longer available</p>
      </div>
    );
  }

  // ─── Opening ──
  if (phase === 'opening') {
    return (
      <div
        className="fixed inset-0 bg-black flex flex-col items-center justify-center px-8 select-none"
        style={{ animation: 'revealFadeIn 0.8s ease' }}
      >
        <p className="text-white/50 text-sm tracking-[0.2em] uppercase mb-6">Your wedding,</p>
        <p className="text-white/50 text-sm tracking-[0.2em] uppercase mb-12">through their eyes</p>

        <p className="text-white text-xl font-light tracking-wide mb-2">{data.event_name}</p>
        <p className="text-white/40 text-xs tracking-wide">
          {formatDate(data.event_date)}{data.event_location ? ` — ${data.event_location}` : ''}
        </p>

        <button
          onClick={() => setPhase(getNextPhase('opening'))}
          className="mt-16 text-white/60 text-sm tracking-wide hover:text-white/90 transition-colors"
          data-testid="reveal-begin"
        >
          Begin &rarr;
        </button>

        <style>{`@keyframes revealFadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
      </div>
    );
  }

  // ─── Love (mixed: text wishes + voice notes) ──
  if (phase === 'love') {
    const mixed = loveMixed[loveIndex];
    if (!mixed) {
      // Edge case — empty
      setPhase(getNextPhase('love'));
      return null;
    }
    const isVoice = mixed.type === 'voice';
    const item = mixed.data;

    const advance = () => {
      if (loveIndex < loveMixed.length - 1) setLoveIndex(i => i + 1);
      else setPhase(getNextPhase('love'));
    };

    const handleVoiceTap = (e) => {
      e.stopPropagation();
      const a = loveAudioRef.current;
      if (!a) return;
      if (voicePlayState === 'idle') {
        a.play().then(() => setVoicePlayState('playing')).catch(() => {
          // If gesture-blocked, try once more on next tap
          setVoicePlayState('idle');
        });
      } else if (voicePlayState === 'playing') {
        a.pause();
        setVoicePlayState('idle');
      } else if (voicePlayState === 'ended') {
        // Replay
        try { a.currentTime = 0; } catch { /* */ }
        a.play().then(() => setVoicePlayState('playing')).catch(() => {});
      }
    };

    return (
      <div
        className="fixed inset-0 bg-black flex flex-col items-center justify-center px-8 select-none"
        onClick={isVoice ? undefined : handleLoveTap}
        data-testid="reveal-love"
      >
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-white/10">
          <div
            className="h-full bg-white/30 transition-all duration-300"
            style={{ width: `${((loveIndex + 1) / loveMixed.length) * 100}%` }}
          />
        </div>

        {!isVoice ? (
          // Text / heart — existing behavior
          <div
            key={loveIndex}
            className="text-center max-w-sm cursor-pointer w-full"
            style={{ animation: 'loveItemFadeIn 0.4s ease' }}
          >
            {item.wish_type === 'heart' ? (
              <span className="text-4xl">{item.emoji || '❤️'}</span>
            ) : (
              <p className="text-white/80 text-lg font-light italic leading-relaxed">
                "{item.message}"
              </p>
            )}
            {item.guest_name && (
              <p className="text-white/30 text-xs mt-4">&mdash; {item.guest_name}</p>
            )}
          </div>
        ) : (
          // Voice — tap to play; auto-advance after playback ends + 500ms pause
          <div
            key={loveIndex}
            className="flex flex-col items-center text-center max-w-sm w-full cursor-pointer"
            style={{ animation: 'loveItemFadeIn 0.4s ease' }}
            onClick={handleVoiceTap}
            data-testid="reveal-love-voice"
          >
            <div className="relative w-28 h-28 flex items-center justify-center mb-8">
              {voicePlayState === 'playing' && (
                <>
                  <span
                    className="absolute inset-0 rounded-full"
                    style={{ border: '1px solid rgba(255,255,255,0.55)', animation: 'loveVoiceBreath 2400ms ease-out infinite' }}
                  />
                  <span
                    className="absolute inset-0 rounded-full"
                    style={{ border: '1px solid rgba(255,255,255,0.35)', animation: 'loveVoiceBreath 2400ms ease-out 800ms infinite' }}
                  />
                </>
              )}
              <span
                className="block w-16 h-16 rounded-full flex items-center justify-center bg-white/10 border border-white/20"
                style={{ transition: 'background-color 200ms', backgroundColor: voicePlayState === 'playing' ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)' }}
              >
                {voicePlayState === 'playing' ? (
                  <span className="text-white text-base">❘❘</span>
                ) : (
                  <span className="text-white text-xl ml-0.5">▶</span>
                )}
              </span>
            </div>
            <p className="text-white/85 text-lg font-light italic leading-relaxed">
              In {item.guest_name}&rsquo;s voice
            </p>
            <p className="text-white/30 text-xs mt-4 tracking-wide">
              {voicePlayState === 'idle' && 'Tap to listen'}
              {voicePlayState === 'playing' && 'Tap to pause'}
              {voicePlayState === 'ended' && 'Tap to replay'}
            </p>
            <audio
              ref={loveAudioRef}
              src={resolveUrl(item.audio_url)}
              preload="auto"
              onEnded={() => {
                setVoicePlayState('ended');
                // 500ms breath then auto-advance
                setTimeout(() => advance(), 500);
              }}
            />
          </div>
        )}

        <style>{`
          @keyframes loveItemFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes loveVoiceBreath {
            0% { transform: scale(0.7); opacity: 0; }
            30% { opacity: 0.5; }
            100% { transform: scale(1.5); opacity: 0; }
          }
        `}</style>
      </div>
    );
  }

  // ─── Transition ──
  if (phase === 'transition') {
    return (
      <div
        className="fixed inset-0 bg-black flex flex-col items-center justify-center px-8 select-none"
        style={{ animation: 'revealFadeIn 0.6s ease' }}
      >
        <p className="text-white/40 text-sm tracking-wide mb-2">And then…</p>
        <p className="text-white/60 text-sm tracking-wide">what they saw</p>
        <style>{`@keyframes revealFadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
      </div>
    );
  }

  // ─── Photos ──
  if (phase === 'photos') {
    const photo = selectedPhotos[photoIndex];
    if (!photo) { setPhase('ending'); return null; }
    const amb = ambienceFor(photo);

    return (
      <div
        className="fixed inset-0 bg-black select-none cursor-pointer"
        onClick={handlePhotoTap}
        data-testid="reveal-photos"
      >
        {/* Progress */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-white/10 z-20">
          <div
            className="h-full bg-white/30 transition-all duration-300"
            style={{ width: `${((photoIndex + 1) / selectedPhotos.length) * 100}%` }}
          />
        </div>

        {/* Photo — full bleed (with subtle warmth for late-night captures) */}
        <img
          src={resolveUrl(photo.image_url)}
          alt=""
          className="absolute inset-0 w-full h-full object-contain transition-all"
          style={{
            opacity: photoVisible ? 1 : 0,
            transform: tapFeedback ? 'scale(1.005)' : (photoVisible ? 'scale(1)' : 'scale(1.01)'),
            transitionDuration: tapFeedback ? '80ms' : '250ms',
            transitionTimingFunction: 'ease-out',
            filter: amb.temp > 0 ? `sepia(${amb.temp}) saturate(1.02)` : undefined,
          }}
          draggable={false}
        />

        {/* Time-of-day amber vignette — barely perceptible, only after dusk */}
        {amb.amber > 0 && (
          <div
            className="absolute inset-0 z-[5] pointer-events-none transition-opacity"
            style={{
              background: 'radial-gradient(ellipse at center, transparent 45%, rgba(255,168,84,0.18) 100%)',
              opacity: photoVisible ? amb.amber : 0,
              transitionDuration: '400ms',
              mixBlendMode: 'soft-light',
            }}
            aria-hidden
          />
        )}

        {/* Bottom gradient for text readability */}
        <div
          className="absolute bottom-0 left-0 right-0 z-10 pointer-events-none"
          style={{ height: '35%', background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)' }}
        />

        {/* Text + attribution — bottom aligned */}
        <div
          className="absolute bottom-0 left-0 right-0 z-20 text-center pointer-events-none"
          style={{
            paddingBottom: 'max(64px, calc(env(safe-area-inset-bottom, 0px) + 56px))',
            paddingLeft: '12%',
            paddingRight: '12%',
          }}
        >
          {photo.note && (
            <p
              className="text-white/80 text-sm font-light italic leading-relaxed mb-2"
              style={{
                opacity: textVisible ? 1 : 0,
                transform: textVisible ? 'translateY(0)' : 'translateY(6px)',
                transition: 'opacity 180ms ease, transform 180ms ease',
              }}
            >
              "{photo.note}"
            </p>
          )}
          {photo.guest_name && (
            <p
              className="text-white/40 text-xs"
              style={{
                opacity: textVisible ? 1 : 0,
                transform: textVisible ? 'translateY(0)' : 'translateY(6px)',
                transition: 'opacity 180ms ease 40ms, transform 180ms ease 40ms',
              }}
            >
              &mdash; {photo.guest_name}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ─── Ending ──
  if (phase === 'ending') {
    return (
      <div
        className="fixed inset-0 bg-black flex flex-col items-center justify-center px-8 select-none"
        style={{ animation: 'revealFadeIn 0.8s ease' }}
      >
        <p className="text-white/60 text-sm tracking-wide mb-2">This was your day.</p>
        <p className="text-white/40 text-xs tracking-wide mb-1">As they saw it.</p>
        <p className="text-white/40 text-xs tracking-wide mb-12">As they felt it.</p>

        {(allMoments.length > 0 || hasAnyNotes) && (
          <div className="flex flex-col items-center gap-4">
            {allMoments.length > 0 && (
              <button
                onClick={() => setPhase('grid')}
                className="text-white/50 text-sm tracking-wide hover:text-white/80 transition-colors"
                data-testid="reveal-see-all"
              >
                See all moments &rarr;
              </button>
            )}
            {hasAnyNotes && (
              <button
                onClick={() => setPhase('notes-full')}
                className="text-white/50 text-sm tracking-wide hover:text-white/80 transition-colors"
                data-testid="reveal-read-words"
              >
                Read their words &rarr;
              </button>
            )}
            {hasVoices && (
              <button
                onClick={() => setPhase('voices-full')}
                className="text-white/50 text-sm tracking-wide hover:text-white/80 transition-colors"
                data-testid="reveal-in-their-voices"
              >
                What they said &rarr;
              </button>
            )}
            {hasAnyKept && (
              <button
                onClick={() => { setKeptIdx(0); setKeptFade(true); setPhase('kept-intro'); }}
                className="text-sm tracking-wide transition-colors"
                style={{
                  color: hasEnoughKept ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.30)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = hasEnoughKept ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.30)'; }}
                data-testid="reveal-what-you-kept"
              >
                Saved moments &rarr;
              </button>
            )}
          </div>
        )}

        {/* Share reveal — subtle secondary link at the very bottom */}
        <div
          className="absolute left-0 right-0 text-center"
          style={{
            bottom: 'max(28px, calc(env(safe-area-inset-bottom, 0px) + 24px))',
            animation: 'shareFadeIn 1.4s ease both',
            animationDelay: '0.8s',
          }}
        >
          <button
            onClick={handleShareReveal}
            className="text-white/30 text-xs tracking-wide hover:text-white/60 transition-colors"
            data-testid="reveal-share"
          >
            {shareState === 'copied' ? 'Link copied' : 'Share your reveal'}
          </button>
        </div>

        <style>{`@keyframes revealFadeIn { from { opacity: 0; } to { opacity: 1; } } @keyframes shareFadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
      </div>
    );
  }

  // ─── Grid (all photos) ──
  if (phase === 'grid') {
    const lightboxPhoto = lightboxIdx !== null ? allMoments[lightboxIdx] : null;
    return (
      <div className="min-h-screen bg-black px-3 pt-12 pb-20">
        <button
          onClick={() => setPhase('ending')}
          className="text-white/40 text-xs mb-6 block"
        >
          &larr; Back
        </button>

        <div className="columns-2 gap-3">
          {allMoments.map((m, i) => {
            const kept = isKept('photo', m.id);
            return (
              <div
                key={m.id}
                className="mb-3 break-inside-avoid w-full block relative group"
                data-testid={`reveal-grid-tile-${m.id}`}
              >
                <button
                  type="button"
                  onClick={() => setLightboxIdx(i)}
                  className="w-full block cursor-pointer"
                  aria-label="Open photo"
                >
                  <img
                    src={resolveUrl(m.image_url)}
                    alt=""
                    className="w-full block rounded-xl"
                    style={{ opacity: 0, transition: 'opacity 0.4s ease' }}
                    onLoad={(e) => { e.target.style.opacity = '1'; }}
                    loading="lazy"
                    draggable={false}
                  />
                </button>
                {/* Quiet in-grid keep heart — available, not demanding.
                    Always present but very subtle when unkept; more visible once kept. */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleKeep('photo', m.id); }}
                  className="absolute top-1.5 right-1.5 w-8 h-8 flex items-center justify-center transition-all active:scale-90"
                  style={{ opacity: kept ? 1 : 0.55 }}
                  data-testid={`reveal-grid-keep-${m.id}`}
                  aria-label={kept ? 'Release' : 'Keep'}
                >
                  <svg
                    key={`grid-heart-${kept}`}
                    width="16" height="16" viewBox="0 0 24 24" fill="none"
                    style={{
                      filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.55))',
                      animation: kept ? 'heartPopGrid 320ms cubic-bezier(0.34, 1.4, 0.5, 1)' : undefined,
                    }}
                  >
                    <path
                      d="M12 20.5s-7.5-4.7-7.5-10a4.5 4.5 0 0 1 8.1-2.7A4.5 4.5 0 0 1 19.5 10.5c0 5.3-7.5 10-7.5 10z"
                      stroke="rgba(255,255,255,0.85)"
                      strokeWidth="1.3"
                      fill={kept ? 'rgba(255,255,255,0.92)' : 'transparent'}
                    />
                  </svg>
                </button>
              </div>
            );
          })}
          <style>{`@keyframes heartPopGrid { 0% { transform: scale(0.95); } 50% { transform: scale(1.12); } 100% { transform: scale(1); } }`}</style>
        </div>

        {/* Lightbox — full-screen soft overlay. Tap anywhere (image or backdrop) to close.
            Heart is the only non-closing interaction. */}
        {lightboxPhoto && (
          <div
            className="fixed inset-0 z-[300] flex items-center justify-center cursor-pointer"
            onClick={() => setLightboxIdx(null)}
            style={{
              backgroundColor: 'rgba(0,0,0,0.82)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              animation: 'lbFade 260ms ease',
            }}
            data-testid="reveal-lightbox"
          >
            <style>{`@keyframes lbFade { from { opacity: 0; } to { opacity: 1; } }`}</style>

            {/* Close (explicit — still available) */}
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
              className="absolute top-5 left-5 text-white/50 text-xs hover:text-white/80 tracking-wide z-10"
            >
              &larr; Back
            </button>

            {/* Keep heart — the ONLY tap that doesn't close */}
            <button
              onClick={(e) => { e.stopPropagation(); toggleKeep('photo', lightboxPhoto.id); }}
              className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center transition-transform active:scale-90 z-10"
              data-testid={`reveal-keep-photo-${lightboxPhoto.id}`}
              aria-label={isKept('photo', lightboxPhoto.id) ? 'Release' : 'Keep'}
            >
              <svg
                key={`heart-${isKept('photo', lightboxPhoto.id)}`}
                width="22" height="22" viewBox="0 0 24 24" fill="none"
                style={{
                  transition: 'fill 220ms ease',
                  animation: isKept('photo', lightboxPhoto.id) ? 'heartPop 360ms cubic-bezier(0.34, 1.4, 0.5, 1)' : undefined,
                }}
              >
                <path
                  d="M12 20.5s-7.5-4.7-7.5-10a4.5 4.5 0 0 1 8.1-2.7A4.5 4.5 0 0 1 19.5 10.5c0 5.3-7.5 10-7.5 10z"
                  stroke="rgba(255,255,255,0.75)"
                  strokeWidth="1.2"
                  fill={isKept('photo', lightboxPhoto.id) ? 'rgba(255,255,255,0.85)' : 'transparent'}
                />
              </svg>
              <style>{`@keyframes heartPop { 0% { transform: scale(0.95); } 50% { transform: scale(1.12); } 100% { transform: scale(1); } }`}</style>
            </button>

            <img
              src={resolveUrl(lightboxPhoto.image_url)}
              alt=""
              className="max-w-[98vw] max-h-[92vh] object-contain rounded-lg select-none"
              style={{ animation: 'lbImgRise 380ms cubic-bezier(0.2, 0.7, 0.3, 1)' }}
              draggable={false}
            />
            <style>{`@keyframes lbImgRise { 0% { opacity: 0; transform: scale(0.98); } 100% { opacity: 1; transform: scale(1); } }`}</style>

            {(lightboxPhoto.note || lightboxPhoto.guest_name) && (
              <div className="absolute left-0 right-0 bottom-8 text-center px-8 pointer-events-none">
                {lightboxPhoto.note && (
                  <p className="text-white/75 text-sm font-light italic mb-1.5">&ldquo;{lightboxPhoto.note}&rdquo;</p>
                )}
                {lightboxPhoto.guest_name && (
                  <p className="text-white/35 text-xs">&mdash; {lightboxPhoto.guest_name}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Ambient limit hint */}
        {limitHint && (
          <div
            className="fixed top-6 left-0 right-0 text-center z-[350] pointer-events-none"
            style={{ animation: 'hintFade 2.2s ease forwards' }}
            data-testid="reveal-keep-limit-hint"
          >
            <style>{`@keyframes hintFade { 0% { opacity: 0; } 15% { opacity: 1; } 80% { opacity: 1; } 100% { opacity: 0; } }`}</style>
            <p className="text-white/55 text-xs tracking-wide italic">You&rsquo;ve kept your moments.</p>
          </div>
        )}
        <KeepFeedbackOverlay
          key={keepFeedback || 'none'}
          mode={keepFeedback}
          onViewKeeps={() => { setLightboxIdx(null); setKeptIdx(0); setKeptFade(true); setPhase('kept-intro'); setKeepFeedback(null); }}
        />
      </div>
    );
  }

  // ─── Read their words (full notes view) ──
  if (phase === 'notes-full') {
    // Full chronological list, but curated/reveal items appear first.
    const revealIds = new Set(love.map(l => l.id));
    const messagesOnly = allLove.filter(n => n.wish_type === 'message' && n.message);
    const inReveal = messagesOnly.filter(n => revealIds.has(n.id));
    const rest = messagesOnly.filter(n => !revealIds.has(n.id))
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const ordered = [...inReveal, ...rest];

    return (
      <div className="min-h-screen bg-black px-6 pt-12 pb-24" data-testid="reveal-notes-full">
        <button
          onClick={() => setPhase('ending')}
          className="text-white/40 text-xs mb-10 block"
        >
          &larr; Back
        </button>

        <p className="text-white/40 text-xs tracking-[0.2em] uppercase text-center mb-10">Their words</p>

        <div className="max-w-xl mx-auto space-y-10">
          {ordered.map((n, i) => {
            const highlighted = revealIds.has(n.id);
            const keptNote = isKept('note', n.id);
            return (
              <div
                key={n.id}
                className="text-center"
                style={{
                  opacity: 0,
                  animation: `noteFade 0.6s ease ${Math.min(i, 8) * 0.05}s forwards`,
                }}
              >
                <p
                  className="font-light italic leading-relaxed"
                  style={{
                    fontSize: highlighted ? '17px' : '15px',
                    color: highlighted ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.55)',
                  }}
                >
                  &ldquo;{n.message}&rdquo;
                </p>
                {n.guest_name && (
                  <p className="text-white/30 text-xs mt-2">&mdash; {n.guest_name}{n.table_name ? ` · ${n.table_name}` : ''}</p>
                )}
                <button
                  onClick={() => toggleKeep('note', n.id)}
                  className="mt-3 inline-flex items-center justify-center w-7 h-7 transition-transform active:scale-90"
                  aria-label={keptNote ? 'Release' : 'Keep'}
                  data-testid={`reveal-keep-note-${n.id}`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 20.5s-7.5-4.7-7.5-10a4.5 4.5 0 0 1 8.1-2.7A4.5 4.5 0 0 1 19.5 10.5c0 5.3-7.5 10-7.5 10z"
                      stroke="rgba(255,255,255,0.55)"
                      strokeWidth="1.2"
                      fill={keptNote ? 'rgba(255,255,255,0.75)' : 'transparent'}
                    />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
        <style>{`@keyframes noteFade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }`}</style>

        {/* Ambient limit hint */}
        {limitHint && (
          <div
            className="fixed top-6 left-0 right-0 text-center z-[350] pointer-events-none"
            style={{ animation: 'hintFade 2.2s ease forwards' }}
            data-testid="reveal-keep-limit-hint-notes"
          >
            <style>{`@keyframes hintFade { 0% { opacity: 0; } 15% { opacity: 1; } 80% { opacity: 1; } 100% { opacity: 0; } }`}</style>
            <p className="text-white/55 text-xs tracking-wide italic">You&rsquo;ve kept your moments.</p>
          </div>
        )}
        <KeepFeedbackOverlay
          key={keepFeedback || 'none'}
          mode={keepFeedback}
          onViewKeeps={() => { setKeptIdx(0); setKeptFade(true); setPhase('kept-intro'); setKeepFeedback(null); }}
        />
      </div>
    );
  }

  // ─── In their voices (browse all received voice messages, tap to play) ──
  if (phase === 'voices-full') {
    return <VoicesFullSurface voices={allVoices} onBack={() => setPhase('ending')} />;
  }

  // ─── Saved moments — intro ──
  if (phase === 'kept-intro') {
    const items = resolvedKept;
    if (items.length === 0) {
      setPhase('ending');
      return null;
    }
    return (
      <div
        className="fixed inset-0 bg-black flex flex-col items-center justify-center px-8 select-none"
        style={{ animation: 'revealFadeIn 0.8s ease' }}
        data-testid="kept-intro"
      >
        <p className="text-white/40 text-sm tracking-[0.2em] uppercase mb-6">Saved moments</p>
        <p className="text-white/50 text-[13px] font-light italic tracking-wide mb-12 max-w-xs text-center leading-relaxed">
          The moments you chose<br/>to hold on to
        </p>
        <p className="text-white/75 text-lg font-light tracking-wide mb-1">{data.event_name}</p>
        {data.event_date && (
          <p className="text-white/30 text-xs tracking-wide">{formatDate(data.event_date)}{data.event_location ? ` — ${data.event_location}` : ''}</p>
        )}
        <button
          onClick={() => { setKeptIdx(0); setKeptFade(true); setPhase('kept-seq'); }}
          className="mt-16 text-white/50 text-sm tracking-wide hover:text-white/80 transition-colors"
          data-testid="kept-begin"
        >
          Begin &rarr;
        </button>
        <style>{`@keyframes revealFadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
      </div>
    );
  }

  // ─── Saved moments — sequence ──
  if (phase === 'kept-seq') {
    const items = resolvedKept;
    if (items.length === 0) {
      setPhase('ending');
      return null;
    }
    const safeIdx = Math.min(keptIdx, items.length - 1);
    const entry = items[safeIdx];
    const advance = () => {
      setKeptFade(false);
      setTimeout(() => {
        if (safeIdx < items.length - 1) {
          setKeptIdx(safeIdx + 1);
          setKeptFade(true);
        } else {
          setPhase('kept-end');
        }
      }, 340);
    };
    const goBack = () => {
      if (safeIdx <= 0) return;
      setKeptFade(false);
      setTimeout(() => {
        setKeptIdx(safeIdx - 1);
        setKeptFade(true);
      }, 340);
    };
    const unkeep = async (e) => {
      e.stopPropagation();
      const k = entry.ref;
      const next = kept.filter(x => !(x.type === k.type && x.id === k.id));
      setKept(next);
      // Roll teach counter back
      try {
        const key = `sitoa_keep_teach_${token}`;
        const prev = Number(sessionStorage.getItem(key) || '0');
        sessionStorage.setItem(key, String(Math.max(0, prev - 1)));
      } catch { /* */ }
      // "Removed" whisper feedback
      if (keepFeedbackTimerRef.current) clearTimeout(keepFeedbackTimerRef.current);
      setKeepFeedback('removed');
      keepFeedbackTimerRef.current = setTimeout(() => setKeepFeedback(null), 1400);
      setKeptFade(false);
      setTimeout(() => {
        if (next.length === 0) { setPhase('ending'); return; }
        const nextIdx = Math.min(safeIdx, next.length - 1);
        setKeptIdx(nextIdx);
        setKeptFade(true);
      }, 340);
      try { await axios.post(`${API}/api/reveal/${token}/keep`, { type: k.type, id: k.id, keep: false }); } catch { /* */ }
    };

    // Left-half / right-half tap for prev / next; center-exclusion zone for heart/close
    const handleZoneTap = (e) => {
      const w = e.currentTarget.clientWidth;
      const x = e.clientX || (e.touches?.[0]?.clientX ?? 0);
      if (x < w * 0.35) goBack();
      else advance();
    };

    return (
      <div
        className="fixed inset-0 bg-black select-none cursor-pointer"
        onClick={handleZoneTap}
        data-testid="kept-seq"
      >
        {/* Progress */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-white/10 z-20">
          <div
            className="h-full bg-white/30 transition-all duration-300"
            style={{ width: `${((safeIdx + 1) / items.length) * 100}%` }}
          />
        </div>

        {/* Close */}
        <button
          onClick={(e) => { e.stopPropagation(); setPhase('kept-end'); }}
          className="absolute top-4 left-4 w-10 h-10 flex items-center justify-center text-white/50 hover:text-white/80 z-30"
          data-testid="kept-seq-close"
          aria-label="Close"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>

        {/* Un-keep heart (filled by default since it IS kept) */}
        <button
          onClick={unkeep}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center z-30 transition-transform active:scale-90"
          data-testid={`kept-seq-unkeep-${entry.item.id}`}
          aria-label="Release"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 20.5s-7.5-4.7-7.5-10a4.5 4.5 0 0 1 8.1-2.7A4.5 4.5 0 0 1 19.5 10.5c0 5.3-7.5 10-7.5 10z"
              stroke="rgba(255,255,255,0.75)"
              strokeWidth="1.2"
              fill="rgba(255,255,255,0.85)"
            />
          </svg>
        </button>

        {/* Content — soft crossfade 340ms */}
        <div
          key={`${entry.kind}-${entry.item.id}`}
          className="absolute inset-0 flex items-center justify-center px-8"
          style={{ opacity: keptFade ? 1 : 0, transition: 'opacity 340ms ease' }}
        >
          {entry.kind === 'note' ? (
            <div className="max-w-xl text-center">
              <p className="text-white/85 text-xl font-light italic leading-relaxed">
                &ldquo;{entry.item.message}&rdquo;
              </p>
              {entry.item.guest_name && (
                <p className="text-white/35 text-xs mt-5">&mdash; {entry.item.guest_name}{entry.item.table_name ? ` · ${entry.item.table_name}` : ''}</p>
              )}
            </div>
          ) : entry.kind === 'voice' ? (
            <KeptVoiceItem item={entry.item} audioUrl={resolveUrl(entry.item.audio_url)} />
          ) : (
            <>
              <img
                src={resolveUrl(entry.item.image_url)}
                alt=""
                className="max-w-[94vw] max-h-[78vh] object-contain rounded-lg"
                draggable={false}
              />
              {(entry.item.note || entry.item.guest_name) && (
                <div className="absolute left-0 right-0 bottom-10 text-center px-10 pointer-events-none">
                  {entry.item.note && (
                    <p className="text-white/75 text-sm font-light italic mb-1.5">&ldquo;{entry.item.note}&rdquo;</p>
                  )}
                  {entry.item.guest_name && (
                    <p className="text-white/35 text-xs">&mdash; {entry.item.guest_name}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <KeepFeedbackOverlay key={keepFeedback || 'none'} mode={keepFeedback} />
      </div>
    );
  }

  // ─── Saved moments — end ──
  if (phase === 'kept-end') {
    const emailValid = /.+@.+\..+/.test(sendEmail.trim());
    return (
      <div
        className="fixed inset-0 bg-black flex flex-col items-center justify-center px-8 select-none"
        style={{ animation: 'revealFadeIn 0.8s ease' }}
        data-testid="kept-end"
      >
        <p className="text-white/70 text-lg font-light italic">You saved these</p>
        <p className="text-white/35 text-[13px] font-light italic tracking-wide mt-2 mb-12">From everything they shared</p>

        <div className="flex flex-col items-center gap-5">
          <a
            href={`${API}/api/reveal/${token}/book`}
            className="text-white/70 text-sm tracking-wide hover:text-white/95 transition-colors"
            data-testid="kept-save-book"
          >
            Save as a book &rarr;
          </a>

          {/* Send it to me — quiet secondary */}
          {sendState === 'idle' && (
            <button
              onClick={() => handleSendBook()}
              className="text-white/55 text-sm tracking-wide hover:text-white/85 transition-colors"
              data-testid="kept-send-to-me"
            >
              Send it to me &rarr;
            </button>
          )}
          {sendState === 'input' && (
            <form
              onSubmit={(e) => { e.preventDefault(); if (emailValid) handleSendBook(sendEmail.trim()); }}
              className="flex items-center gap-3"
              data-testid="kept-send-input-row"
            >
              <input
                type="email"
                value={sendEmail}
                onChange={(e) => setSendEmail(e.target.value)}
                placeholder="Email address"
                autoFocus
                className="bg-transparent border-0 border-b border-white/20 focus:border-white/50 text-white/80 text-sm py-1 px-1 w-56 outline-none placeholder:text-white/25"
                data-testid="kept-send-email-input"
              />
              <button
                type="submit"
                disabled={!emailValid}
                className="text-white/70 text-sm tracking-wide hover:text-white/95 transition-opacity disabled:opacity-30"
                data-testid="kept-send-submit"
              >
                Send
              </button>
            </form>
          )}
          {sendState === 'sending' && (
            <p className="text-white/50 text-sm tracking-wide italic" data-testid="kept-send-state">Sending…</p>
          )}
          {sendState === 'sent' && (
            <p
              className="text-white/55 text-sm tracking-wide italic"
              data-testid="kept-send-sent"
              style={{ animation: 'sentFade 4.5s ease forwards' }}
            >
              <style>{`@keyframes sentFade { 0% { opacity: 0; } 10% { opacity: 1; } 80% { opacity: 1; } 100% { opacity: 0; } }`}</style>
              Sent{sentTo ? ` to ${sentTo}` : ''}
            </p>
          )}
          {sendState === 'error' && (
            <p className="text-white/40 text-xs italic">Couldn&rsquo;t send right now</p>
          )}

          <button
            onClick={() => setPhase('kept-edit')}
            className="text-white/45 text-sm tracking-wide hover:text-white/70 transition-colors mt-3"
            data-testid="kept-edit-enter"
          >
            Edit
          </button>
          <button
            onClick={() => setPhase('ending')}
            className="text-white/45 text-sm tracking-wide hover:text-white/70 transition-colors"
            data-testid="kept-done"
          >
            Done
          </button>
        </div>
        <style>{`@keyframes revealFadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
      </div>
    );
  }

  // ─── Saved moments — edit (lightweight) ──
  if (phase === 'kept-edit') {
    const items = resolvedKept;
    if (items.length === 0) {
      setPhase('ending');
      return null;
    }
    const move = (idx, dir) => {
      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= kept.length) return;
      const next = [...kept];
      [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
      setKept(next);
      saveKeptOrder(next);
    };
    const remove = (k) => {
      const next = kept.filter(x => !(x.type === k.type && x.id === k.id));
      setKept(next);
      saveKeptOrder(next);
    };
    return (
      <div className="min-h-screen bg-black px-6 pt-12 pb-24" data-testid="kept-edit">
        <button
          onClick={() => setPhase('kept-end')}
          className="text-white/40 text-xs mb-10 block"
        >
          &larr; Done
        </button>
        <p className="text-white/40 text-xs tracking-[0.2em] uppercase text-center mb-10">Edit</p>
        <div className="max-w-md mx-auto space-y-3">
          {items.map((entry, i) => {
            const k = entry.ref;
            return (
              <div
                key={`${k.type}-${k.id}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
                style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                data-testid={`kept-edit-row-${k.id}`}
              >
                {/* Thumbnail */}
                <div className="w-12 h-12 rounded-md overflow-hidden flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                  {entry.kind === 'photo' ? (
                    <img src={resolveUrl(entry.item.image_url)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 6h16M4 12h16M4 18h10" stroke="rgba(255,255,255,0.45)" strokeWidth="1.3" strokeLinecap="round"/></svg>
                  )}
                </div>
                {/* Preview */}
                <div className="flex-1 min-w-0">
                  {entry.kind === 'note' ? (
                    <p className="text-white/75 text-sm truncate italic">&ldquo;{entry.item.message}&rdquo;</p>
                  ) : (
                    <p className="text-white/60 text-sm truncate">{entry.item.note || entry.item.guest_name || 'Photo'}</p>
                  )}
                  {entry.item.guest_name && entry.kind === 'note' && (
                    <p className="text-white/35 text-[11px] truncate">&mdash; {entry.item.guest_name}</p>
                  )}
                </div>
                {/* Controls */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white/80 disabled:opacity-20"
                    data-testid={`kept-edit-up-${k.id}`}
                    aria-label="Move up"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 14l6-6 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </button>
                  <button
                    onClick={() => move(i, 1)}
                    disabled={i === items.length - 1}
                    className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white/80 disabled:opacity-20"
                    data-testid={`kept-edit-down-${k.id}`}
                    aria-label="Move down"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 10l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </button>
                  <button
                    onClick={() => remove(k)}
                    className="w-7 h-7 flex items-center justify-center text-white/30 hover:text-white/70"
                    data-testid={`kept-edit-remove-${k.id}`}
                    aria-label="Remove"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return null;
}
